import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { registerToastHandlers } from "./toast";
import { btn } from "../Button/buttonStyles";

const ToastContext = createContext(null);

let nextId = 1;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  // { message, confirmText, cancelText, resolve } | null
  const [confirmState, setConfirmState] = useState(null);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message, { type = "success", duration = 4000 } = {}) => {
      const id = nextId++;
      setToasts((prev) => [...prev, { id, message, type }]);
      if (duration > 0) {
        setTimeout(() => dismiss(id), duration);
      }
      return id;
    },
    [dismiss]
  );

  // Promise-based confirm so callers can `await confirmDialog(...)`.
  const confirm = useCallback(
    (message, { confirmText = "Confirm", cancelText = "Cancel" } = {}) =>
      new Promise((resolve) => {
        setConfirmState({ message, confirmText, cancelText, resolve });
      }),
    []
  );

  const resolveConfirm = useCallback((result) => {
    setConfirmState((prev) => {
      prev?.resolve(result);
      return null;
    });
  }, []);

  // Expose push/confirm to the imperative bridge so non-hook code can use them.
  useEffect(() => {
    registerToastHandlers({ push, confirm });
  }, [push, confirm]);

  // Stable across renders so consumers can list it as a hook dependency.
  const toast = useMemo(
    () => ({
      success: (msg, opts) => push(msg, { ...opts, type: "success" }),
      error: (msg, opts) => push(msg, { ...opts, type: "error" }),
      info: (msg, opts) => push(msg, { ...opts, type: "info" }),
    }),
    [push]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
      {confirmState && (
        <ConfirmDialog
          state={confirmState}
          onCancel={() => resolveConfirm(false)}
          onConfirm={() => resolveConfirm(true)}
        />
      )}
    </ToastContext.Provider>
  );
};

// Same shape as components/Modals/ConfirmModal, for the promise-based
// `toast.confirm(...)` callers.
const ConfirmDialog = ({ state, onCancel, onConfirm }) => (
  <div
    className="fixed inset-0 z-[110] flex items-center justify-center bg-gray-900/40 px-4"
    role="dialog"
    aria-modal="true"
  >
    <div className="w-full max-w-sm overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-pop">
      <div className="flex items-start gap-3 px-5 pb-5 pt-5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-danger-50 text-danger-600">
          <AlertTriangle size={17} strokeWidth={2} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold tracking-tight text-gray-900">Confirm</h3>
          <p className="mt-1 text-[13px] leading-relaxed text-gray-500">{state.message}</p>
        </div>
      </div>
      <div className="flex justify-end gap-2 border-t border-gray-100 bg-gray-50/70 px-5 py-3">
        <button onClick={onCancel} className={btn("secondary", "sm")}>
          {state.cancelText}
        </button>
        <button onClick={onConfirm} className={btn("danger", "sm")}>
          {state.confirmText}
        </button>
      </div>
    </div>
  </div>
);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
};

const ToastViewport = ({ toasts, onDismiss }) => (
  <div className="fixed top-5 right-5 z-[100] flex flex-col gap-2 pointer-events-none">
    {toasts.map((t) => (
      <ToastItem key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
    ))}
  </div>
);

const ICONS = {
  success: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  ),
  error: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
        clipRule="evenodd"
      />
    </svg>
  ),
  info: (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  ),
};

const STYLES = {
  success: "border-l-4 border-l-success-600 border-gray-200",
  error: "border-l-4 border-l-danger-500 border-gray-200",
  info: "border-l-4 border-l-brand-500 border-gray-200",
};

const ICON_COLORS = {
  success: "text-success-700",
  error: "text-danger-500",
  info: "text-brand-500",
};

const ToastItem = ({ toast, onDismiss }) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      className={`pointer-events-auto flex min-w-[280px] max-w-sm items-start gap-3 rounded-xl border bg-white px-4 py-3 text-gray-800 shadow-pop transition-all duration-200 ${
        STYLES[toast.type] || STYLES.info
      } ${visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`}
      role="status"
    >
      <span className={`shrink-0 mt-0.5 ${ICON_COLORS[toast.type] || ICON_COLORS.info}`}>
        {ICONS[toast.type] || ICONS.info}
      </span>
      <p className="flex-1 text-[13px] leading-snug">{toast.message}</p>
      <button
        onClick={onDismiss}
        className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};
